import {
    ADD_LA_PAYMENT_CARD_FAIL,
    ADD_LA_PAYMENT_CARD_REQUEST,
    ADD_LA_PAYMENT_CARD_SUCCESS,
    ADD_ZIP_PAYMENT_CARD_FAIL,
    ADD_ZIP_PAYMENT_CARD_REQUEST,
    ADD_ZIP_PAYMENT_CARD_SUCCESS,
    DISMISS_MODAL,
    LOAD_LA_PAYMENT_CARD_FAIL,
    LOAD_LA_PAYMENT_CARD_REQUEST,
    LOAD_LA_PAYMENT_CARD_SUCCESS,
    LOG_OUT_BIDDER,
    UPDATE_LA_PAYMENT_CARD_FAIL,
    UPDATE_LA_PAYMENT_CARD_REQUEST,
    UPDATE_LA_PAYMENT_CARD_SUCCESS,
} from './actions';
import { Address } from '@liveauctioneers/caterwaul-components/types/Address';
import { createSelector } from '@reduxjs/toolkit';
import { CreditCard } from '@liveauctioneers/caterwaul-components/types/CreditCard';
import { fetchBidderDataIfNeeded, getAuthToken, getBidderId } from './user';
import { getDeployment } from './config';
import { getLAPaymentCards, postAddLACard, postAddZipCard, postUpdateLACard } from '@/redux/api/payment';
import { GlobalState } from '../rootReducer';
import { PaymentProviders } from '@/enums';
import ms from 'ms';

const REDUX_STORE_TIME = ms('20m');

// reducer
export const DEFAULT_STATE = {
    addSubmitted: false,
    addSuccess: false,
    allowedToAddCards: true,
    error: '',
    laPaymentCards: [],
    loaded: 0,
    loading: false,
    updateSubmitted: false,
    updateSuccess: false,
    zipCard: null,
};

export type State = typeof DEFAULT_STATE & {
    error: string;
    loaded: number;
    zipCard?: CreditCard;
};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case ADD_LA_PAYMENT_CARD_REQUEST:
            return {
                ...state,
                addSubmitted: true,
                addSuccess: false,
                error: '',
                loading: false,
            };
        case ADD_LA_PAYMENT_CARD_SUCCESS:
            return {
                ...state,
                addSubmitted: false,
                addSuccess: true,
                loaded: 0,
            };
        case ADD_ZIP_PAYMENT_CARD_REQUEST:
            return {
                ...state,
                addSubmitted: true,
                addSuccess: false,
                error: undefined,
            };
        case ADD_ZIP_PAYMENT_CARD_SUCCESS:
            return {
                ...state,
                addSubmitted: false,
                addSuccess: true,
                zipCard: action.payload,
            };
        case LOAD_LA_PAYMENT_CARD_REQUEST:
            return {
                ...state,
                loaded: 0,
                loading: true,
            };
        case LOAD_LA_PAYMENT_CARD_SUCCESS:
            return {
                ...state,
                allowedToAddCards: action.payload.allowedToAddCards,
                error: undefined,
                laPaymentCards: action.payload.cards,
                loaded: action.meta.actionTime,
                loading: false,
            };
        case ADD_LA_PAYMENT_CARD_FAIL:
        case ADD_ZIP_PAYMENT_CARD_FAIL:
        case LOAD_LA_PAYMENT_CARD_FAIL:
        case UPDATE_LA_PAYMENT_CARD_FAIL:
            return {
                ...state,
                addSubmitted: false,
                addSuccess: false,
                error: action.payload,
                loading: false,
                updateSubmitted: false,
            };
        case UPDATE_LA_PAYMENT_CARD_REQUEST:
            return {
                ...state,
                error: undefined,
                updateSubmitted: true,
                updateSuccess: false,
            };
        case UPDATE_LA_PAYMENT_CARD_SUCCESS:
            return {
                ...state,
                error: undefined,
                loaded: 0,
                updateSubmitted: false,
                updateSuccess: false,
            };
        case DISMISS_MODAL:
            return {
                ...state,
                addSubmitted: false,
                addSuccess: false,
                error: '',
                loading: false,
            };
        case LOG_OUT_BIDDER:
            return DEFAULT_STATE;
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.creditCard;
const passthroughSelector = (state: GlobalState, id) => id;

export const getAllowedToAddCards = createSelector([stateSelector], (state) => state.allowedToAddCards);
export const getLAPaymentCardsLoaded = createSelector([stateSelector], (state) => state.loaded);
export const getLAPaymentCardsLoading = createSelector([stateSelector], (state) => state.loading);
export const getLAPaymentCardsAddSubmitted = createSelector([stateSelector], (state) => state.addSubmitted);
export const getLAPaymentCardsAddSuccess = createSelector([stateSelector], (state) => state.addSuccess);
export const getLAPaymentCardsUpdateSubmitted = createSelector([stateSelector], (state) => state.updateSubmitted);
export const getLAPaymentCardsUpdateSuccess = createSelector([stateSelector], (state) => state.updateSuccess);
export const getCreditCardError = createSelector([stateSelector], (state) => state.error);
export const getZipCard = createSelector([stateSelector], (state) => state.zipCard);

export const creditCardsAnyLoadingSelector = createSelector(
    [getLAPaymentCardsLoading, getLAPaymentCardsAddSubmitted, getLAPaymentCardsUpdateSubmitted],
    (loading, addSubmitted, updateSubmitted) => loading || addSubmitted || updateSubmitted
);

/**
 * returns all cards on file
 * @function laPaymentCardsSelector
 * @category Credit Card Service
 * @param {GlobalState}, number
 */
export const laPaymentCardsSelector = createSelector([stateSelector], (state) => state.laPaymentCards);

export const laPaymentCardsForProviderSelector = createSelector(
    [laPaymentCardsSelector, passthroughSelector],
    (allLaPaymentCards, providerId) => allLaPaymentCards?.filter((cof) => cof.providerId === providerId)
);

/**
 * returns first matching card on file that is a defaultCard and matches providerId
 * @function laPaymentDefaultCardSelector
 * @category Credit Card Service
 * @param {GlobalState}, number
 */
export const laPaymentDefaultCardSelector = createSelector(
    [laPaymentCardsSelector, passthroughSelector],
    (laPaymentCards, providerId) => laPaymentCards?.find((card) => card.defaultCard && card.providerId === providerId)
);

//TODO: Should eventually check based on the authed card status.
export const getHasValidCardOnFile = createSelector([laPaymentDefaultCardSelector], (laPaymentCard) =>
    Boolean(laPaymentCard?.token)
);

export const getShouldCollectBillingAddress = (state: any, paymentProvider: number) => {
    const creditCard = laPaymentDefaultCardSelector(state, paymentProvider);
    return Boolean(creditCard) && !Boolean(creditCard.address.address1);
};

/* ACTION CREATORS */
export type SubmitAddCreditCardParams = {
    address: Address;
    cardToken: string;
    cardholderName: { first: string; last: string };
    houseId: number;
    providerId: PaymentProviders;
};
export const submitAddCreditCard =
    ({
        address,
        cardholderName,
        cardToken,
        houseId,
        providerId = PaymentProviders.Payrix,
    }: SubmitAddCreditCardParams) =>
    async (dispatch: Function, getState: Function) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const bidderId = getBidderId(state);

        // Ensure bidder profile is loaded
        if (bidderId > 0) {
            await dispatch(fetchBidderDataIfNeeded(authToken, true));
        }

        return await dispatch(addLACreditCard({ address, cardholderName, cardToken, houseId, providerId }));
    };

type AddLACreditCardParams = {
    address: Address;
    cardToken: string;
    cardholderName: { first: string; last: string };
    houseId: number;
    providerId: number;
};
//add is for creating credit cards for all providers.
const addLACreditCard =
    ({ address, cardholderName, cardToken, houseId, providerId }: AddLACreditCardParams) =>
    async (dispatch: Function, getState: Function) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const bidderId = getBidderId(state);
            const deployment = getDeployment(state);
            dispatch({
                payload: bidderId,
                type: ADD_LA_PAYMENT_CARD_REQUEST,
            });
            const response = await postAddLACard({
                authToken,
                bidderId,
                billingAddress: address,
                cardholderName: `${cardholderName.first} ${cardholderName.last}`,
                cardNonce: cardToken,
                deployment,
                houseId,
                providerId,
            });

            dispatch({
                meta: { actionTime: Date.now() },
                payload: response.payload,
                type: ADD_LA_PAYMENT_CARD_SUCCESS,
            });

            // Update user profile
            await dispatch(fetchBidderDataIfNeeded(authToken, true));
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: ADD_LA_PAYMENT_CARD_FAIL,
            });
            //reject the promise specifically because if it resolves the error doesn't appear in the registration modal.
            return Promise.reject();
        }
    };

export type AddZipCreditCardParams = {
    billingAddress: Address;
    brand: string;
    firstName: string;
    houseId: number;
    lastName: string;
    month: number;
    number: string;
    providerId: number;
    securityCode: string;
    year: string;
};
//add is for creating new zip cards.
export const addZipCard =
    ({
        billingAddress,
        brand,
        firstName,
        houseId,
        lastName,
        month,
        number,
        providerId,
        securityCode,
        year,
    }: AddZipCreditCardParams) =>
    async (dispatch: Function, getState: Function) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const bidderId = getBidderId(state);
            const deployment = getDeployment(state);
            dispatch({
                payload: bidderId,
                type: ADD_ZIP_PAYMENT_CARD_REQUEST,
            });
            const response = await postAddZipCard({
                authToken,
                bidderId,
                billingAddress,
                brand,
                deployment,
                firstName,
                houseId,
                lastName,
                month,
                number,
                providerId,
                securityCode,
                year,
            });

            dispatch({
                meta: { actionTime: Date.now() },
                payload: response.payload,
                type: ADD_ZIP_PAYMENT_CARD_SUCCESS,
            });

            return Promise.resolve(response.payload);
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: ADD_ZIP_PAYMENT_CARD_FAIL,
            });
        }
    };

type UpdateLACreditCardParams = {
    billingAddress?: Address;
    cardId: number;
    defaultCard: boolean;
    enabled: boolean;
};
//Update is for updating the billing address of an existing card. The api can be used for disabling cards too, but the ui has no hookup for that.
export const updateLACreditCard =
    ({ billingAddress, cardId, defaultCard, enabled }: UpdateLACreditCardParams) =>
    async (dispatch: Function, getState: Function) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const bidderId = getBidderId(state);
            const deployment = getDeployment(state);
            dispatch({
                payload: bidderId,
                type: UPDATE_LA_PAYMENT_CARD_REQUEST,
            });

            // Send the request
            const response = await postUpdateLACard({
                authToken,
                bidderId,
                billingAddress,
                cardId,
                defaultCard,
                deployment,
                enabled,
            });
            dispatch({
                payload: response.payload,
                type: UPDATE_LA_PAYMENT_CARD_SUCCESS,
            });
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: UPDATE_LA_PAYMENT_CARD_FAIL,
            });
        }
    };

export const fetchLAPaymentCard = () => async (dispatch: Function, getState: Function) => {
    const state = getState();
    try {
        const authToken = getAuthToken(state);
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);

        // if bidderId is less than 1 the bidder data hasnt loaded yet and we can return.
        if (bidderId < 1) {
            return Promise.resolve();
        }

        dispatch({
            type: LOAD_LA_PAYMENT_CARD_REQUEST,
        });
        const response = await getLAPaymentCards({ authToken, bidderId, deployment });
        return dispatch({
            meta: { actionTime: Date.now() },
            payload: response.payload,
            type: LOAD_LA_PAYMENT_CARD_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: LOAD_LA_PAYMENT_CARD_FAIL,
        });
    }
};

const shouldFetchLAPaymentCards = createSelector(
    getLAPaymentCardsLoaded,
    getLAPaymentCardsLoading,
    (loaded: number, loading: boolean) => {
        if (loading) {
            return false;
        }
        const time = Date.now();
        const diff = time - loaded;
        return !Boolean(loaded) || diff > REDUX_STORE_TIME;
    }
);

export const fetchLAPaymentCardIfNeeded = () => async (dispatch: Function, getState: Function) => {
    const state = getState();
    if (shouldFetchLAPaymentCards(state)) {
        dispatch(fetchLAPaymentCard());
    } else {
        return Promise.resolve();
    }
};
